'use client';

import React, { useEffect, useState } from 'react';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { brandLanding } from '@/constants/routes';

import { CategoryMegaMenuChildren } from './_components/CategoryMegaMenuChildren';
import { CategoryMegaMenuParents } from './_components/CategoryMegaMenuParents';
import { CategoryMegaMenuSkeleton } from './_components/CategoryMegaMenuSkeleton';
import { CategoryMegaMenuProps } from './models';

import { getCategoryBrandsService } from '@/client/services/core/brand/methods';
import { CategoryMegaMenuLink } from '@/types/categoryMegaMenu';

export const CategoryMegaMenu = ({ open, categories, isDark, onNavigate }: CategoryMegaMenuProps) => {
    const [activeCategoryId, setActiveCategoryId] = useState<number | null>(categories[0]?.id ?? null);
    const [batteryBrands, setBatteryBrands] = useState<Record<number, CategoryMegaMenuLink[]>>({});
    const batteryCategoryIds = categories
        .filter((category) => category.slug === CAR_BATTERY_VEHICLE_TYPE_ID)
        .flatMap((category) => category.subCategories)
        .filter((category) => category.slug === CAR_BATTERY_VEHICLE_TYPE_ID || category.slug === MOTOR_BATTERY_VEHICLE_TYPE_ID)
        .map((category) => category.categoryId)
        .filter((categoryId): categoryId is number => categoryId !== undefined && categoryId > 0);
    const batteryCategoryIdsKey = [...new Set(batteryCategoryIds)].sort((a, b) => a - b).join(',');

    useEffect(() => {
        if (!open || !batteryCategoryIdsKey) {
            return;
        }

        let active = true;

        batteryCategoryIdsKey
            .split(',')
            .map(Number)
            .forEach((categoryId) => {
                const fetchBrands = async () => {
                    try {
                        const response = await getCategoryBrandsService({ categoryId, includeChildren: true, activeOnly: true });

                        if (active) {
                            setBatteryBrands((current) => ({
                                ...current,
                                [categoryId]: (response.data.content ?? []).map((brand) => ({
                                    id: brand.id,
                                    name: brand.name,
                                    slug: brand.slug,
                                    href: `${brandLanding}/${brand.slug}`
                                }))
                            }));
                        }
                    } catch {
                        if (active) {
                            setBatteryBrands((current) => ({ ...current, [categoryId]: [] }));
                        }
                    }
                };

                void fetchBrands();
            });

        return () => {
            active = false;
        };
    }, [open, batteryCategoryIdsKey]);

    useEffect(() => {
        if (!categories.length) {
            setActiveCategoryId(null);
            return;
        }

        setActiveCategoryId((currentId) => {
            if (currentId && categories.some((category) => category.id === currentId)) {
                return currentId;
            }

            return categories[0].id;
        });
    }, [categories]);

    if (!open) {
        return null;
    }

    const selectedCategory = categories.find((category) => category.id === activeCategoryId) ?? categories[0];
    const activeCategory =
        selectedCategory?.slug === CAR_BATTERY_VEHICLE_TYPE_ID
            ? {
                  ...selectedCategory,
                  subCategories: selectedCategory.subCategories.map((subCategory) => ({
                      ...subCategory,
                      children:
                          subCategory.categoryId !== undefined
                              ? (batteryBrands[subCategory.categoryId] ?? subCategory.children)
                              : subCategory.children
                  }))
              }
            : selectedCategory;

    return (
        <div className='absolute right-0 top-full z-[60] pt-2'>
            <div
                className={`w-[min(920px,calc(100vw-2rem))] overflow-hidden rounded-lg border shadow-lg
                    ${isDark ? 'border-neutral-700 bg-[#111827]' : 'border-gray-200 bg-white'}`}>
                {!categories.length ? (
                    <CategoryMegaMenuSkeleton isDark={isDark} />
                ) : (
                    <div className='flex h-[360px]'>
                        <CategoryMegaMenuParents
                            categories={categories}
                            activeCategoryId={activeCategoryId}
                            isDark={isDark}
                            onHoverCategory={setActiveCategoryId}
                            onNavigate={onNavigate}
                        />
                        <CategoryMegaMenuChildren activeCategory={activeCategory} isDark={isDark} onNavigate={onNavigate} />
                    </div>
                )}
            </div>
        </div>
    );
};
